import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import '../products/EffyOrder.css';
import './EffyOrderPricing.css';
import { EFFY_ORDER_PLANS as PLANS, formatPlanPrice, getPlanPriceForCycle, BILLING_MONTHLY, BILLING_ANNUAL } from './effyOrderPlans';
import BillingCycleToggle from './BillingCycleToggle';

const FEATURES = [
  {
    category: 'Order Details',
    rows: [
      { label: 'Add multiple products to a single order', lite: true, pro: true, growth: true, premium: true },
      { label: 'View complete order — items, quantities, prices & customer info', lite: true, pro: true, growth: true, premium: true },
      { label: 'Edit order — update items, quantities & prices anytime', lite: true, pro: true, growth: true, premium: true },
      { label: 'Add notes or remarks to an order', lite: true, pro: true, growth: true, premium: true },
      { label: 'Discount per item (flat amount or percentage)', lite: true, pro: true, growth: true, premium: true },
      { label: 'Overall order-level discount', lite: false, pro: true, growth: true, premium: true },
      { label: 'Track payment status (Paid, Partially Paid, Pending, Refunded)', lite: true, pro: true, growth: true, premium: true },
      { label: 'Track order progress (Initiated → In Progress → Completed)', lite: true, pro: true, growth: true, premium: true },
      { label: 'Set and track expected delivery date', lite: true, pro: true, growth: true, premium: true },
      { label: 'GST tax applied automatically on orders (CGST / SGST / IGST)', lite: false, pro: true, growth: true, premium: true },
      { label: 'See outstanding balance for a customer on the order', lite: false, pro: true, growth: true, premium: true },
      { label: 'POS barcode scan — scan a product to add it to a sale order instantly', lite: false, pro: false, growth: true, premium: true },
    ],
  },
  {
    category: 'Orders',
    rows: [
      { label: 'Monthly orders', lite: '500 orders/mo', pro: 'Unlimited', growth: 'Unlimited', premium: 'Unlimited' },
      { label: 'Import multiple orders at once via spreadsheet', lite: false, pro: true, growth: true, premium: true },
      { label: 'Search orders by customer name, phone or invoice number', lite: true, pro: true, growth: true, premium: true },
      { label: 'Filter orders by status or payment status', lite: true, pro: true, growth: true, premium: true },
      { label: 'Download / export orders as Excel', lite: false, pro: true, growth: true, premium: true },
      { label: 'Instantly notify customers when an order is placed or updated', lite: false, pro: true, growth: true, premium: true },
      { label: 'Send invoice directly to customer on WhatsApp', lite: false, pro: true, growth: true, premium: true },
      { label: 'Automatic reminders for pending or unpaid orders', lite: false, pro: false, growth: false, premium: true },
    ],
  },
  {
    category: 'Inventory',
    rows: [
      { label: 'Products in inventory', lite: '50 items', pro: '1,000 items', growth: '1,000 items', premium: 'Unlimited' },
      { label: 'Track stock across multiple locations or warehouses', lite: true, pro: true, growth: true, premium: true },
      { label: 'Stock automatically reduces when an order is placed', lite: true, pro: true, growth: true, premium: true },
      { label: 'Alerts when a product is running low', lite: false, pro: true, growth: true, premium: true },
      { label: 'Add product photos', lite: false, pro: false, growth: 'Up to 3 per item', premium: 'Up to 3 per item' },
      { label: 'Generate & print barcodes for products', lite: false, pro: true, growth: true, premium: true },
      { label: 'Add many products at once via spreadsheet', lite: false, pro: true, growth: true, premium: true },
    ],
  },
  {
    category: 'Customers',
    rows: [
      { label: 'Customer records', lite: '100 customers', pro: '5,000 customers', growth: '5,000 customers', premium: 'Unlimited' },
      { label: 'See complete order history for each customer', lite: true, pro: true, growth: true, premium: true },
      { label: 'Customer profile created automatically on first order', lite: true, pro: true, growth: true, premium: true },
      { label: 'View total outstanding dues per customer', lite: false, pro: true, growth: true, premium: true },
      { label: 'GST number validation — check a customer GSTIN and auto-fill name, address & state', lite: false, pro: false, growth: true, premium: true },
      { label: 'Warning when a GST number is inactive or the name does not match', lite: false, pro: false, growth: true, premium: true },
      { label: 'Sales report broken down by customer', lite: false, pro: false, growth: false, premium: true },
    ],
  },
  {
    category: 'Billing & Documents',
    rows: [
      { label: 'Professional PDF invoices', lite: true, pro: true, growth: true, premium: true },
      { label: 'GST-ready invoices with full tax breakdown', lite: false, pro: true, growth: true, premium: true },
      { label: 'Quotation / estimate document for customers', lite: false, pro: true, growth: true, premium: true },
      { label: 'Delivery challan for your delivery team', lite: false, pro: true, growth: true, premium: true },
      { label: 'Tax profiles for different product categories', lite: '3 profiles', pro: '50 profiles', growth: '50 profiles', premium: 'Unlimited' },
      { label: 'Add your shop logo to invoices & documents', lite: false, pro: true, growth: true, premium: true },
    ],
  },
  {
    category: 'Reports & Analytics',
    rows: [
      { label: 'Dashboard with monthly & daily sales charts', lite: true, pro: true, growth: true, premium: true },
      { label: 'Download full sales report', lite: false, pro: true, growth: true, premium: true },
      { label: 'Download GST report for tax filing', lite: false, pro: true, growth: true, premium: true },
      { label: 'Filter reports by any date range', lite: false, pro: true, growth: true, premium: true },
      { label: 'Filter reports by order or payment status', lite: false, pro: true, growth: true, premium: true },
    ],
  },
  {
    category: 'Customer Notifications',
    rows: [
      { label: 'Send order updates to customers via email', lite: false, pro: true, growth: true, premium: true },
      { label: 'Send order updates to customers via WhatsApp', lite: false, pro: true, growth: true, premium: true },
      { label: 'WhatsApp notification volume', lite: false, pro: 'Limited', growth: 'Limited', premium: 'Unlimited' },
      { label: 'Automatic follow-up for pending or unpaid orders', lite: false, pro: false, growth: false, premium: true },
    ],
  },
  {
    category: 'Team & Access',
    rows: [
      { label: 'Owner login', lite: true, pro: true, growth: true, premium: true },
      { label: 'Add employees and track every sale by the employee who made it', lite: false, pro: true, growth: true, premium: true },
      { label: 'Per-employee sales report with date range and totals', lite: false, pro: true, growth: true, premium: true },
      { label: 'Choose the salesperson ("Sold By") on each sale order', lite: false, pro: true, growth: true, premium: true },
      { label: 'Additional user accounts you can create for your team', lite: false, pro: false, growth: '1 account', premium: '2 accounts' },
    ],
  },
  {
    category: 'Support & Customisation',
    rows: [
      { label: 'Email support', lite: true, pro: true, growth: true, premium: true },
      { label: 'Priority support with fast response time', lite: false, pro: true, growth: true, premium: true },
      { label: 'Dedicated support team assigned to your account', lite: false, pro: false, growth: false, premium: true },
      { label: 'Custom feature development built into your product', lite: false, pro: false, growth: false, premium: true },
      { label: 'Guided onboarding & setup assistance', lite: false, pro: false, growth: false, premium: true },
      { label: 'Committed response & resolution time', lite: false, pro: false, growth: false, premium: true },
    ],
  },
];

function CellValue({ value }) {
  if (value === true) return <span className="pricing-check" aria-label="Included">&#10003;</span>;
  if (value === false) return <span className="pricing-dash" aria-label="Not included">&mdash;</span>;
  return <span className="pricing-text-val">{value}</span>;
}

export default function EffyOrderPricing() {
  const [openCategory, setOpenCategory] = useState(null);
  const [billingCycle, setBillingCycle] = useState(BILLING_MONTHLY);
  const isYearly = billingCycle === BILLING_ANNUAL;

  // Price block of one plan card: struck-out original + discounted price when yearly.
  const renderPriceBlock = (plan) => {
    const prices = getPlanPriceForCycle(plan, billingCycle);
    if (!isYearly) {
      return (
        <div className="oi-plan-card__price">
          <span className="oi-plan-card__currency"><span className="oi-plan-card__symbol">₹</span>{Number(plan.amount).toLocaleString('en-IN')}</span>
          <span className="oi-plan-card__period">/{plan.period}</span>
        </div>
      );
    }
    return (
      <>
        <div className="oi-plan-card__original"><s>{formatPlanPrice(prices.original)}</s></div>
        <div className="oi-plan-card__price">
          <span className="oi-plan-card__currency"><span className="oi-plan-card__symbol">₹</span>{Number(prices.discounted).toLocaleString('en-IN')}</span>
          <span className="oi-plan-card__period">/year</span>
        </div>
        <span className="oi-plan-card__free-pill">1 month free</span>
        <div className="oi-plan-card__save">Save {formatPlanPrice(prices.saved)}</div>
      </>
    );
  };

  const toggleCategory = (cat) => setOpenCategory(prev => prev === cat ? null : cat);

  const scrollToComparison = (e) => {
    e.preventDefault();
    const el = document.getElementById('plan-comparison');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pricing-page">
      <Helmet>
        <title>Pricing | EffyVenture — Simple, Transparent Plans</title>
        <meta
          name="description"
          content="Choose the EffyVenture plan that fits your business. Start with Lite, grow with Pro or Growth, or unlock unlimited scale and dedicated support with Premium."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.effyventure.com/effy-order-pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.effyventure.com/effy-order-pricing" />
        <meta property="og:site_name" content="EffyVenture" />
        <meta property="og:title" content="Pricing | EffyVenture — Simple, Transparent Plans" />
        <meta
          property="og:description"
          content="Choose the EffyVenture plan that fits your business. Start with Lite, grow with Pro or Growth, or unlock unlimited scale and dedicated support with Premium."
        />
        <meta property="og:image" content="https://www.effyventure.com/logo/logo-512.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Pricing | EffyVenture — Simple, Transparent Plans" />
        <meta
          name="twitter:description"
          content="Choose the EffyVenture plan that fits your business. Start with Lite, grow with Pro or Growth, or unlock unlimited scale and dedicated support with Premium."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="pricing-hero">
        <div className="pricing-hero__content">
          <div className="pricing-hero__tag">Pricing</div>
          <h1 className="pricing-hero__title">
            Simple pricing,<br />
            <span className="pricing-hero__accent">no surprises</span>
          </h1>
          <p className="pricing-hero__sub">
            Start with a 30-day free trial on Lite or Pro. Every plan includes order tracking and inventory management. GST invoicing is available from the Pro plan onwards — built for Indian small businesses.
          </p>
        </div>
      </section>

      {/* ── Plan Cards ── */}
      <section className="oi-pricing-v2">
        <div className="oi-pricing-v2__container">
          <div className="oi-pricing-v2__toggle">
            <BillingCycleToggle
              value={billingCycle}
              onChange={setBillingCycle}
              monthlyLabel="Monthly"
              yearlyLabel="Yearly"
              saveBadge="1 month free"
            />
          </div>
          <div className="oi-pricing-v2__grid">
            {PLANS.map((plan) => (
              <div key={plan.id} className={`oi-plan-card ${plan.highlight ? 'oi-plan-card--highlight' : ''}`}>
                {plan.highlight && <div className="oi-plan-card__badge">Most Popular</div>}
                <h3 className="oi-plan-card__name">{plan.name}</h3>
                {renderPriceBlock(plan)}
                {plan.trialText && (
                  <div className="oi-plan-card__trial">{plan.trialText}</div>
                )}
                {plan.gstNote && (
                  <div className="oi-plan-card__gst-note">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="13" height="13">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                    {plan.gstNote}
                  </div>
                )}
                <p className="oi-plan-card__desc">{plan.description}</p>
                <ul className="oi-plan-card__features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.ctaRoute || '/products/order-tracking/signup'}
                  className={`btn-v2 ${plan.highlight ? 'btn-v2--primary' : plan.id === 'premium' ? 'btn-v2--premium-sales' : 'btn-v2--secondary'} oi-plan-card__cta`}
                >
                  {plan.cta}
                </Link>
                <a href="#plan-comparison" className="oi-plan-card__compare" onClick={scrollToComparison}>
                  Compare plans
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Full feature comparison ── */}
      <section id="plan-comparison" className="pricing-comparison">
        <div className="pricing-comparison__inner">
          <div className="pricing-comparison__header">
            <h2>Full feature comparison</h2>
            <p>See exactly what is included in each plan across every product area.</p>
          </div>
          <div className="pricing-table">
            <div className="pricing-table__head">
              <div className="pricing-table__head-feature">Feature</div>
              {PLANS.map((plan) => (
                <div
                  key={plan.id}
                  className={`pricing-table__head-col ${plan.highlight ? 'pricing-table__head-col--highlight' : ''}`}
                >
                  <span>{plan.name}</span>
                </div>
              ))}
            </div>
            {FEATURES.map((group) => {
              const isOpen = openCategory === group.category;
              return (
                <div key={group.category} className="pricing-table__group">
                  <button
                    className="pricing-table__group-header"
                    onClick={() => toggleCategory(group.category)}
                    aria-expanded={isOpen}
                  >
                    <span className="pricing-table__group-name">{group.category}</span>
                    <span className={`pricing-table__group-caret ${isOpen ? 'pricing-table__group-caret--open' : ''}`}>
                      &#8964;
                    </span>
                  </button>
                  {isOpen && group.rows.map((row, ri) => (
                    <div key={ri} className={`pricing-table__row ${ri % 2 === 0 ? 'pricing-table__row--alt' : ''}`}>
                      <div className="pricing-table__feature-label">{row.label}</div>
                      <div className="pricing-table__cell"><CellValue value={row.lite} /></div>
                      <div className="pricing-table__cell pricing-table__cell--highlight"><CellValue value={row.pro} /></div>
                      <div className="pricing-table__cell"><CellValue value={row.growth} /></div>
                      <div className="pricing-table__cell"><CellValue value={row.premium} /></div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="pricing-faq">
        <div className="pricing-faq__inner">
          <h2>Frequently asked questions</h2>
          <div className="pricing-faq__grid">
            {FAQ.map((q, i) => (
              <FaqItem key={i} q={q.q} a={q.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="pricing-bottom-cta">
        <div className="pricing-bottom-cta__inner">
          <h2>Not sure which plan fits?</h2>
          <p>
            Start with the Lite plan and upgrade when you are ready. Our team is happy to walk you
            through the right option for your business.
          </p>
          <div className="pricing-bottom-cta__actions">
            <Link to="/contact" className="btn btn-primary" style={{ fontSize: 15, padding: '13px 30px' }}>
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const FAQ = [
  {
    q: 'Is there a free trial?',
    a: 'Yes. The Lite and Pro plans both come with a 30-day free trial — no payment required upfront. You can explore all the features of your chosen plan during the trial period and upgrade, downgrade, or cancel at any time.',
  },
  {
    q: 'Can I switch plans at any time?',
    a: 'Yes. You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.',
  },
  {
    q: 'What happens if I exceed my Lite-plan order limit?',
    a: 'New orders will be paused until the next month or until you upgrade to Pro, Growth or Premium. Existing orders and data are never deleted.',
  },
  {
    q: 'Which plans include GST invoicing?',
    a: 'GST invoicing is available from the Pro plan onwards. Pro, Growth and Premium plans generate fully GST-compliant invoices — with automatic tax breakdowns for intra-state and inter-state orders, your shop logo, and support for multiple tax categories. The Lite plan creates clean PDF invoices without GST. Quotations and delivery challans are also available from Pro onwards. GST number validation (look up a customer GSTIN and auto-fill their details) is available on Growth and Premium.',
  },
  {
    q: 'How does the Premium plan custom feature development work?',
    a: 'Once you subscribe to Premium, your dedicated support team will schedule a discovery call. We scope, build, and deploy custom features — such as tailored report formats, workflow automations, or business-specific integrations — directly into your account at no additional cost.',
  },
  {
    q: 'What do I get with the Growth plan?',
    a: 'Growth is Pro plus the tools a counter-sales shop needs: POS barcode scanning on sale orders, GST number validation, up to 3 photos per product, and 1 additional user account for a teammate.',
  },
  {
    q: 'Can I track what my staff sell?',
    a: 'Yes, on the Pro, Growth and Premium plans. Add employees, pick the salesperson on each sale order, and see each employee\'s sales and totals for any date range.',
  },
  {
    q: 'How many user accounts can I create?',
    a: 'Every plan has the owner login. Growth lets you add 1 more user account and Premium lets you add 2. Lite and Pro are owner-login only.',
  },
  {
    q: 'Is my data safe and private?',
    a: 'Absolutely. Every account is fully isolated — your orders, inventory, and customer data are never shared with or visible to other businesses. Each account operates in its own secure, private environment.',
  },
  {
    q: 'Do you offer annual billing?',
    a: 'Yes. Choose Yearly and pay for 11 months to get 12 — one month is free.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`pricing-faq__item ${open ? 'pricing-faq__item--open' : ''}`}>
      <button className="pricing-faq__q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <span className={`pricing-faq__caret ${open ? 'pricing-faq__caret--open' : ''}`}>&#8964;</span>
      </button>
      {open && <p className="pricing-faq__a">{a}</p>}
    </div>
  );
}
